/** Default C# custom indicator: multi-pane showcase (main MA + Momentum MACD + Volume trend). */
export const DEFAULT_INDICATOR_CODE = `using System;
using Vybe.Indicators;

[Pane(Name = "Momentum", Height = 120)]
[Pane(Name = "Volume", Height = 100)]
[Dashboard(
    Title = "TREND DASHBOARD",
    Position = "TopRight",
    PositionInput = "DashboardPosition",
    ShowInput = "ShowDashboard",
    BackgroundColorInput = "DashboardBg",
    BorderColorInput = "DashboardBorder",
    OpacityInput = "DashboardOpacity",
    ColumnHeaders = "Metric,Status")]
[Indicator(
    Name = "Trend Dashboard",
    Version = "2.3",
    Overlay = true,
    Description = "Multi-pane showcase: trend MA, MACD momentum, volume spikes, buy/sell signals, supply/demand zones, volume profile + heatmap, stats dashboard table")]
public class TrendDashboard : Indicator
{
    [Input(Label = "Fast period", Min = 1, Max = 500, Step = 1, Group = "MA")]
    public int FastPeriod { get; set; } = 10;

    [Input(Label = "Slow period", Min = 1, Max = 500, Step = 1, Group = "MA")]
    public int SlowPeriod { get; set; } = 30;

    [Input(Label = "Show fast MA", Group = "Visibility")]
    public bool ShowFast { get; set; } = false;

    [Input(Label = "Show slow MA", Group = "Visibility")]
    public bool ShowSlow { get; set; } = false;

    [Input(Label = "Fast MA color", Group = "Colors")]
    public Color FastColor { get; set; } = "#2962FF";

    [Input(Label = "Slow MA color", Group = "Colors")]
    public Color SlowColor { get; set; } = "#FF6D00";

    [Input(Label = "Up color", Group = "Colors")]
    public Color UpColor { get; set; } = "#26A69A";

    [Input(Label = "Down color", Group = "Colors")]
    public Color DownColor { get; set; } = "#FF6D00";

    [Input(Label = "Change candle color", Group = "Candle")]
    public bool ChangeCandleColor { get; set; } = false;

    [Input(Label = "Up body", Group = "Candle colors")]
    public Color UpBody { get; set; } = "#26A69A";

    [Input(Label = "Up border", Group = "Candle colors")]
    public Color UpBorder { get; set; } = "#1B5E20";

    [Input(Label = "Up wick", Group = "Candle colors")]
    public Color UpWick { get; set; } = "#2E7D32";

    [Input(Label = "Down body", Group = "Candle colors")]
    public Color DownBody { get; set; } = "#EF5350";

    [Input(Label = "Down border", Group = "Candle colors")]
    public Color DownBorder { get; set; } = "#B71C1C";

    [Input(Label = "Down wick", Group = "Candle colors")]
    public Color DownWick { get; set; } = "#C62828";

    [Input(Label = "Neutral body", Group = "Candle colors")]
    public Color NeutralBody { get; set; } = "#9E9E9E";

    [Input(Label = "Neutral border", Group = "Candle colors")]
    public Color NeutralBorder { get; set; } = "#616161";

    [Input(Label = "Neutral wick", Group = "Candle colors")]
    public Color NeutralWick { get; set; } = "#757575";

    [Input(Label = "Fast EMA", Min = 1, Max = 100, Step = 1, Group = "Momentum")]
    public int FastEma { get; set; } = 12;

    [Input(Label = "Slow EMA", Min = 1, Max = 100, Step = 1, Group = "Momentum")]
    public int SlowEma { get; set; } = 26;

    [Input(Label = "Signal EMA", Min = 1, Max = 100, Step = 1, Group = "Momentum")]
    public int SignalEma { get; set; } = 9;

    [Input(Label = "Hollow histogram bodies", Group = "Momentum")]
    public bool EnableHollowBodies { get; set; } = true;

    [Input(Label = "Hist up color", Group = "Momentum colors")]
    public Color HistUpColor { get; set; } = "#26A69A";

    [Input(Label = "Hist down color", Group = "Momentum colors")]
    public Color HistDownColor { get; set; } = "#EF5350";

    [Input(Label = "MACD line color", Group = "Momentum colors")]
    public Color MacdLineColor { get; set; } = "#2962FF";

    [Input(Label = "Signal line color", Group = "Momentum colors")]
    public Color SignalLineColor { get; set; } = "#FF6D00";

    [Input(Label = "Volume MA period", Min = 1, Max = 200, Step = 1, Group = "Volume")]
    public int VolMaPeriod { get; set; } = 20;

    [Input(Label = "Spike multiplier", Min = 1, Max = 10, Step = 0.1, Group = "Volume")]
    public double SpikeMult { get; set; } = 2.0;

    [Input(Label = "Vol up", Group = "Volume colors")]
    public Color VolUpColor { get; set; } = "#26A69A";

    [Input(Label = "Vol down", Group = "Volume colors")]
    public Color VolDownColor { get; set; } = "#EF5350";

    [Input(Label = "Vol spike up", Group = "Volume colors")]
    public Color VolSpikeUpColor { get; set; } = "#00E676";

    [Input(Label = "Vol spike down", Group = "Volume colors")]
    public Color VolSpikeDownColor { get; set; } = "#FF1744";

    [Input(Label = "Volume MA color", Group = "Volume colors")]
    public Color VolMaColor { get; set; } = "#AB47BC";

    [Input(Label = "Show signals", Group = "Signals")]
    public bool ShowSignals { get; set; } = true;

    [Input(Label = "Show signal labels", Group = "Signals")]
    public bool ShowSignalLabels { get; set; } = true;

    [Input(Label = "Show signal prices", Group = "Signals")]
    public bool ShowSignalPrices { get; set; } = false;

    [Input(Label = "Buy signal color", Group = "Signal colors")]
    public Color BuySignalColor { get; set; } = "#26A69A";

    [Input(Label = "Sell signal color", Group = "Signal colors")]
    public Color SellSignalColor { get; set; } = "#EF5350";

    [Input(Label = "Show zones", Group = "Zones")]
    public bool ShowZones { get; set; } = true;

    [Input(Label = "Swing lookback", Min = 2, Max = 50, Step = 1, Group = "Zones")]
    public int Lookback { get; set; } = 8;

    [Input(Label = "Demand zone color", Group = "Zone colors")]
    public Color DemandColor { get; set; } = "rgba(38,166,154,0.25)";

    [Input(Label = "Supply zone color", Group = "Zone colors")]
    public Color SupplyColor { get; set; } = "rgba(239,83,80,0.25)";

    [Input(Label = "Broken zone color", Group = "Zone colors")]
    public Color BrokenZoneColor { get; set; } = "rgba(158,158,158,0.20)";

    [Input(Label = "Show volume profile", Group = "Volume Profile / Heatmap")]
    public bool ShowProfile { get; set; } = true;

    [Input(Label = "Show heatmap", Group = "Volume Profile / Heatmap")]
    public bool ShowHeatmap { get; set; } = true;

    [Input(Label = "Price bins", Min = 8, Max = 200, Step = 1, Group = "Volume Profile / Heatmap")]
    public int Bins { get; set; } = 60;

    [Input(Label = "Profile width %", Min = 5, Max = 50, Step = 1, Group = "Volume Profile / Heatmap")]
    public double ProfileWidthPct { get; set; } = 28;

    [Input(Label = "Profile buy color", Group = "Volume Profile / Heatmap")]
    public Color ProfileBuyColor { get; set; } = "#2196F3";

    [Input(Label = "Profile sell color", Group = "Volume Profile / Heatmap")]
    public Color ProfileSellColor { get; set; } = "#FFB300";

    [Input(Label = "Show dashboard", Group = "Dashboard")]
    public bool ShowDashboard { get; set; } = true;

    [Input(Label = "Dashboard position", Group = "Dashboard")]
    public string DashboardPosition { get; set; } = "TopRight";

    [Input(Label = "Dashboard opacity", Min = 0.1, Max = 1, Step = 0.05, Group = "Dashboard")]
    public double DashboardOpacity { get; set; } = 0.92;

    [Input(Label = "Dashboard background", Group = "Dashboard")]
    public Color DashboardBg { get; set; } = "rgba(18,18,24,0.92)";

    [Input(Label = "Dashboard border", Group = "Dashboard")]
    public Color DashboardBorder { get; set; } = "rgba(255,255,255,0.12)";

    // ── Main pane ──────────────────────────────────────────
    [Plot(
        Label = "MA",
        Width = 2,
        Style = "Line",
        Colors = "#26A69A,#FF6D00",
        ColorInputs = "UpColor,DownColor")]
    public Series Ma;

    [PlotColorIndex(Plot = "Ma")]
    public Series MaColor;

    [Plot(Label = "Fast MA", Color = "#2962FF", Width = 1, Style = "Dotted", ColorInput = "FastColor")]
    public Series Fast;

    [Plot(Label = "Slow MA", Color = "#FF6D00", Width = 1, Style = "Line", ColorInput = "SlowColor")]
    public Series Slow;

    [CandleColor(
        BodyColors = "#26A69A,#EF5350,#9E9E9E",
        BorderColors = "#1B5E20,#B71C1C,#616161",
        WickColors = "#2E7D32,#C62828,#757575",
        BodyColorInputs = "UpBody,DownBody,NeutralBody",
        BorderColorInputs = "UpBorder,DownBorder,NeutralBorder",
        WickColorInputs = "UpWick,DownWick,NeutralWick")]
    public Series CandleState;

    [Signal(
        BuyColor = "#26A69A",
        SellColor = "#EF5350",
        BuyColorInput = "BuySignalColor",
        SellColorInput = "SellSignalColor",
        BuyText = "BUY",
        SellText = "SELL",
        ShowInput = "ShowSignals",
        ShowLabelsInput = "ShowSignalLabels",
        ShowPriceInput = "ShowSignalPrices")]
    public Series SignalState;

    [Zone(
        Top = "ZoneTop",
        Bottom = "ZoneBottom",
        DemandColor = "rgba(38,166,154,0.25)",
        SupplyColor = "rgba(239,83,80,0.25)",
        BrokenColor = "rgba(158,158,158,0.20)",
        DemandColorInput = "DemandColor",
        SupplyColorInput = "SupplyColor",
        BrokenColorInput = "BrokenZoneColor",
        ShowInput = "ShowZones")]
    public Series ZoneState;

    public Series ZoneTop;
    public Series ZoneBottom;

    [PriceHistogram(
        Buy = "BuyVol",
        Sell = "SellVol",
        Bins = 60,
        BinsInput = "Bins",
        BuyColor = "#2196F3",
        SellColor = "#FFB300",
        BuyColorInput = "ProfileBuyColor",
        SellColorInput = "ProfileSellColor",
        ProfileWidthPct = 28,
        ProfileWidthPctInput = "ProfileWidthPct",
        ShowProfileInput = "ShowProfile",
        ShowHeatmapInput = "ShowHeatmap")]
    public Series PriceHistState;

    public Series BuyVol;
    public Series SellVol;

    [DashboardCell(
        Label = "Trend",
        Row = 1,
        Texts = "BEARISH,NEUTRAL,BULLISH",
        TextColors = "#EF5350,#B0BEC5,#26A69A",
        BackgroundColors = "rgba(239,83,80,0.35),rgba(158,158,158,0.25),rgba(38,166,154,0.35)")]
    public Series DashTrend;

    [DashboardCell(
        Label = "MA Cross",
        Row = 2,
        Texts = "BEARISH,NONE,BULLISH",
        TextColors = "#EF5350,#78909C,#26A69A",
        BackgroundColors = "rgba(239,83,80,0.30),rgba(96,125,139,0.20),rgba(38,166,154,0.30)")]
    public Series DashMaCross;

    [DashboardCell(
        Label = "Momentum",
        Row = 3,
        Texts = "BEARISH,NEUTRAL,BULLISH",
        TextColors = "#EF5350,#B0BEC5,#26A69A",
        BackgroundColors = "rgba(239,83,80,0.30),rgba(158,158,158,0.20),rgba(38,166,154,0.30)")]
    public Series DashMomentum;

    [DashboardCell(
        Label = "Volume",
        Row = 4,
        Texts = "LOW,NORMAL,HIGH",
        TextColors = "#78909C,#FFB300,#00E676",
        BackgroundColors = "rgba(96,125,139,0.20),rgba(255,179,0,0.25),rgba(0,230,118,0.30)")]
    public Series DashVolume;

    [DashboardCell(
        Label = "Signal",
        Row = 5,
        Texts = "NONE,BUY,SELL",
        TextColors = "#78909C,#26A69A,#EF5350",
        BackgroundColors = "rgba(96,125,139,0.15),rgba(38,166,154,0.30),rgba(239,83,80,0.30)")]
    public Series DashSignal;

    [DashboardCell(
        Label = "Verdict",
        Row = 6,
        Texts = "SELL,NEUTRAL,BUY,STRONG BUY",
        TextColors = "#EF5350,#B0BEC5,#26A69A,#00E676",
        BackgroundColors = "rgba(239,83,80,0.35),rgba(158,158,158,0.25),rgba(38,166,154,0.35),rgba(0,230,118,0.40)")]
    public Series DashVerdict;

    // ── Momentum pane ──────────────────────────────────────
    [Plot(
        Label = "Histogram",
        Pane = "Momentum",
        Style = "Histogram",
        BaseValue = 0,
        Colors = "#26A69A,#EF5350",
        ColorInputs = "HistUpColor,HistDownColor")]
    public Series MacdHist;

    [PlotColorIndex(Plot = "MacdHist")]
    public Series MacdHistColor;

    [PlotBarStyle(Plot = "MacdHist")]
    public Series MacdHistBarStyle;

    [Plot(Label = "MACD", Pane = "Momentum", Color = "#2962FF", Width = 1, Style = "Line", ColorInput = "MacdLineColor")]
    public Series MacdLine;

    [Plot(Label = "Signal", Pane = "Momentum", Color = "#FF6D00", Width = 1, Style = "Dashed", ColorInput = "SignalLineColor")]
    public Series SignalLine;

    // ── Volume pane ────────────────────────────────────────
    [Plot(
        Label = "Volume",
        Pane = "Volume",
        Style = "Histogram",
        Colors = "#26A69A,#EF5350,#00E676,#FF1744",
        ColorInputs = "VolUpColor,VolDownColor,VolSpikeUpColor,VolSpikeDownColor")]
    public Series Vol;

    [PlotColorIndex(Plot = "Vol")]
    public Series VolColor;

    [Plot(Label = "Vol MA", Pane = "Volume", Color = "#AB47BC", Width = 1, Style = "Line", ColorInput = "VolMaColor")]
    public Series VolMa;

    private double _fastSum;
    private double _slowSum;
    private double _volSum;
    private double _emaFast;
    private double _emaSlow;
    private double _emaSignal;
    private bool _haveEmaFast;
    private bool _haveEmaSlow;
    private bool _haveEmaSignal;
    private double _prevFast;
    private double _prevSlow;
    private bool _havePrevMa;
    private double _activeZoneTop = double.NaN;
    private double _activeZoneBottom = double.NaN;
    private int _activeZoneState = -1;

    protected override void OnInit()
    {
        Logger.Print($"TrendDashboard OnInit — Fast={FastPeriod}, Slow={SlowPeriod}, MACD={FastEma}/{SlowEma}/{SignalEma}");
    }

    protected override void OnCalculate(int index)
    {
        // Volume-at-price companions (always computed for profile/heatmap)
        BuyVol[index] = Close[index] >= Open[index] ? Volume[index] : 0;
        SellVol[index] = Close[index] < Open[index] ? Volume[index] : 0;

        var fast = CalcSmaRolling(Close, FastPeriod, index, ref _fastSum);
        var slow = CalcSmaRolling(Close, SlowPeriod, index, ref _slowSum);

        if (double.IsNaN(fast) || double.IsNaN(slow))
        {
            Ma[index] = double.NaN;
            MaColor[index] = double.NaN;
            Fast[index] = double.NaN;
            Slow[index] = double.NaN;
            CandleState[index] = double.NaN;
            SignalState[index] = double.NaN;
            ZoneState[index] = double.NaN;
            ZoneTop[index] = double.NaN;
            ZoneBottom[index] = double.NaN;
            MacdHist[index] = double.NaN;
            MacdHistColor[index] = double.NaN;
            MacdHistBarStyle[index] = 0;
            MacdLine[index] = double.NaN;
            SignalLine[index] = double.NaN;
            Vol[index] = double.NaN;
            VolColor[index] = double.NaN;
            VolMa[index] = double.NaN;
            DashTrend[index] = double.NaN;
            DashMaCross[index] = double.NaN;
            DashMomentum[index] = double.NaN;
            DashVolume[index] = double.NaN;
            DashSignal[index] = double.NaN;
            DashVerdict[index] = double.NaN;
            return;
        }

        Ma[index] = (fast + slow) / 2.0;
        MaColor[index] = fast >= slow ? 0 : 1;
        Fast[index] = ShowFast ? fast : double.NaN;
        Slow[index] = ShowSlow ? slow : double.NaN;

        if (!ChangeCandleColor)
            CandleState[index] = -1;
        else
        {
            var close = Close[index];
            if (close > fast && fast > slow)
                CandleState[index] = 0;
            else if (close < fast && fast < slow)
                CandleState[index] = 1;
            else
                CandleState[index] = 2;
        }

        // Buy/sell signals on fast/slow MA cross
        if (index > 0 && _havePrevMa && !double.IsNaN(_prevFast) && !double.IsNaN(_prevSlow))
        {
            if (_prevFast <= _prevSlow && fast > slow)
                SignalState[index] = 0;
            else if (_prevFast >= _prevSlow && fast < slow)
                SignalState[index] = 1;
            else
                SignalState[index] = -1;
        }
        else
            SignalState[index] = -1;

        _prevFast = fast;
        _prevSlow = slow;
        _havePrevMa = true;

        // Supply/demand zones from swing pivots
        UpdateZones(index);

        // Self-computed EMA MACD (Momentum pane)
        var emaFast = CalcEma(Close[index], FastEma, ref _emaFast, ref _haveEmaFast, index);
        var emaSlow = CalcEma(Close[index], SlowEma, ref _emaSlow, ref _haveEmaSlow, index);

        if (index < SlowEma - 1)
        {
            MacdLine[index] = double.NaN;
            SignalLine[index] = double.NaN;
            MacdHist[index] = double.NaN;
            MacdHistColor[index] = double.NaN;
            MacdHistBarStyle[index] = 0;
        }
        else
        {
            var macd = emaFast - emaSlow;
            MacdLine[index] = macd;
            var signal = CalcEma(macd, SignalEma, ref _emaSignal, ref _haveEmaSignal, index);
            SignalLine[index] = signal;

            if (index < SlowEma + SignalEma - 2)
            {
                MacdHist[index] = double.NaN;
                MacdHistColor[index] = double.NaN;
                MacdHistBarStyle[index] = 0;
            }
            else
            {
                var hist = macd - signal;
                MacdHist[index] = hist;
                MacdHistColor[index] = hist >= 0 ? 0 : 1;
                var prevHist = index > 0 ? MacdHist[index - 1] : double.NaN;
                MacdHistBarStyle[index] = HistogramBarStyleIndex(hist, prevHist, EnableHollowBodies);
            }
        }

        // Volume pane — spike-colored histogram + volume SMA
        var vol = Volume[index];
        var volMa = CalcSmaRolling(Volume, VolMaPeriod, index, ref _volSum);
        Vol[index] = vol;
        VolMa[index] = volMa;

        if (double.IsNaN(volMa))
        {
            VolColor[index] = double.NaN;
        }
        else
        {
            var upBar = Close[index] >= Open[index];
            var spike = vol > SpikeMult * volMa;
            if (upBar && spike)
                VolColor[index] = 2;
            else if (!upBar && spike)
                VolColor[index] = 3;
            else if (upBar)
                VolColor[index] = 0;
            else
                VolColor[index] = 1;
        }

        UpdateDashboardCells(index, fast, slow, vol, volMa);
    }

    private void UpdateDashboardCells(int index, double fast, double slow, double vol, double volMa)
    {
        if (!ShowDashboard)
        {
            DashTrend[index] = double.NaN;
            DashMaCross[index] = double.NaN;
            DashMomentum[index] = double.NaN;
            DashVolume[index] = double.NaN;
            DashSignal[index] = double.NaN;
            DashVerdict[index] = double.NaN;
            return;
        }

        // Trend: 0=bearish, 1=neutral, 2=bullish
        var close = Close[index];
        if (close > fast && fast > slow)
            DashTrend[index] = 2;
        else if (close < fast && fast < slow)
            DashTrend[index] = 0;
        else
            DashTrend[index] = 1;

        // MA cross state on this bar: 0=bearish cross, 1=none, 2=bullish cross
        var sig = SignalState[index];
        if (sig >= 0 && Math.Round(sig) == 0)
            DashMaCross[index] = 2;
        else if (sig >= 0 && Math.Round(sig) == 1)
            DashMaCross[index] = 0;
        else
            DashMaCross[index] = 1;

        // Momentum from MACD histogram
        var hist = MacdHist[index];
        var prevHist = index > 0 ? MacdHist[index - 1] : double.NaN;
        if (double.IsNaN(hist))
            DashMomentum[index] = 1;
        else if (hist > 0 && !double.IsNaN(prevHist) && hist >= prevHist)
            DashMomentum[index] = 2;
        else if (hist < 0 && !double.IsNaN(prevHist) && hist <= prevHist)
            DashMomentum[index] = 0;
        else
            DashMomentum[index] = 1;

        // Volume: 0=low, 1=normal, 2=high/spike
        if (double.IsNaN(volMa) || volMa <= 0)
            DashVolume[index] = 1;
        else if (vol > SpikeMult * volMa)
            DashVolume[index] = 2;
        else if (vol < volMa * 0.6)
            DashVolume[index] = 0;
        else
            DashVolume[index] = 1;

        // Signal: 0=none, 1=buy, 2=sell
        if (sig < 0 || double.IsNaN(sig))
            DashSignal[index] = 0;
        else if (Math.Round(sig) == 0)
            DashSignal[index] = 1;
        else
            DashSignal[index] = 2;

        // Verdict score 0..3
        var score = 0;
        if (DashTrend[index] >= 2) score += 2;
        else if (DashTrend[index] >= 1) score += 1;
        if (DashMomentum[index] >= 2) score += 1;
        else if (DashMomentum[index] <= 0) score -= 1;
        if (DashVolume[index] >= 2) score += 1;
        if (DashSignal[index] == 1) score += 2;
        else if (DashSignal[index] == 2) score -= 2;

        if (score >= 4)
            DashVerdict[index] = 3;
        else if (score >= 2)
            DashVerdict[index] = 2;
        else if (score <= -2)
            DashVerdict[index] = 0;
        else
            DashVerdict[index] = 1;
    }

    private void UpdateZones(int index)
    {
        if (!ShowZones)
        {
            ZoneState[index] = -1;
            ZoneTop[index] = double.NaN;
            ZoneBottom[index] = double.NaN;
            return;
        }

        var pivot = index - 1;
        if (pivot >= Lookback)
        {
            if (IsSwingLow(pivot, Lookback))
            {
                var barLow = Low[pivot];
                var barHigh = High[pivot];
                var height = Math.Max(barHigh - barLow, Close[pivot] * 0.0005);
                _activeZoneBottom = barLow;
                _activeZoneTop = barLow + height * 2.0;
                _activeZoneState = 0;
            }
            else if (IsSwingHigh(pivot, Lookback))
            {
                var barLow = Low[pivot];
                var barHigh = High[pivot];
                var height = Math.Max(barHigh - barLow, Close[pivot] * 0.0005);
                _activeZoneTop = barHigh;
                _activeZoneBottom = barHigh - height * 2.0;
                _activeZoneState = 1;
            }
        }

        if (_activeZoneState >= 0 &&
            !double.IsNaN(_activeZoneTop) && !double.IsNaN(_activeZoneBottom))
        {
            if (_activeZoneState < 2)
            {
                var close = Close[index];
                if (_activeZoneState == 0 && close < _activeZoneBottom)
                    _activeZoneState = 2;
                else if (_activeZoneState == 1 && close > _activeZoneTop)
                    _activeZoneState = 2;
            }

            ZoneTop[index] = _activeZoneTop;
            ZoneBottom[index] = _activeZoneBottom;
            ZoneState[index] = _activeZoneState;
        }
        else
        {
            ZoneTop[index] = double.NaN;
            ZoneBottom[index] = double.NaN;
            ZoneState[index] = -1;
        }
    }

    private bool IsSwingLow(int pivot, int lookback)
    {
        var low = Low[pivot];
        for (int j = pivot - lookback; j < pivot; j++)
            if (Low[j] <= low)
                return false;
        return true;
    }

    private bool IsSwingHigh(int pivot, int lookback)
    {
        var high = High[pivot];
        for (int j = pivot - lookback; j < pivot; j++)
            if (High[j] >= high)
                return false;
        return true;
    }

    private double CalcEma(double value, int period, ref double ema, ref bool haveEma, int index)
    {
        if (index == 0)
            haveEma = false;

        if (PrevCalculated > 0 && index == PrevCalculated - 1)
            haveEma = false;

        var k = 2.0 / (period + 1);
        if (!haveEma)
        {
            ema = value;
            haveEma = true;
            return ema;
        }

        ema = (value - ema) * k + ema;
        return ema;
    }

    /// <summary>O(1) rolling SMA; reseeds at incremental junction (PrevCalculated - 1).</summary>
    private double CalcSmaRolling(Series source, int period, int index, ref double sum)
    {
        if (index == 0)
            sum = 0;

        if (PrevCalculated > 0 && index == PrevCalculated - 1)
        {
            sum = 0;
            int from = index - period + 1;
            if (from < 0) from = 0;
            for (int i = from; i <= index; i++)
                sum += source[i];
        }
        else
        {
            sum += source[index];
            if (index >= period)
                sum -= source[index - period];
        }

        if (index < period - 1)
            return double.NaN;

        return sum / period;
    }

    protected override void OnDeinit()
    {
        Logger.Print("TrendDashboard OnDeinit");
    }
}
`;
